import { DateOnly } from '@if/core/date'
import { EPaymentMethod } from '@if/core/types'
import {
  ControlledCheckbox,
  ControlledDateOnlyPicker,
  ControlledNumberField,
  ControlledSelect,
  ControlledTextField,
  useDollarAdornment,
} from '@if/mui'
import { Stack } from '@mui/material'
import FormDialog from '@src/components/Common/Dialog/FormDialog'
import { useUnmountableDialog } from '@src/services/dialog'
import { useUser } from '@src/providers/UserProvider'
import { SafePaymentActionDateService } from '@src/services/SafePaymentActionDateService'
import { JSX, useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import EditPaymentCloseAllBtn from '../EditPaymentCloseAllBtn'
import { EditPaymentSchemaType } from '../EditPayments-schema'
import { useAddPaymentSchema } from './LoanAddPayment-schema'

interface LoanAddPaymentDialogProps {
  onConfirm: (data: EditPaymentSchemaType) => void
  onCloseAll: () => void
  onClose: () => void
  paymentMethodOptions: JSX.Element[]
  loanActivationDate: DateOnly
  initialAmount?: number
}

const LoanAddPaymentDialog = ({
  onConfirm,
  onCloseAll,
  onClose,
  paymentMethodOptions,
  loanActivationDate,
  initialAmount,
}: LoanAddPaymentDialogProps) => {
  const { t } = useTranslation()
  const user = useUser()
  const canBypassAcpLock = user?.rights.canEditLockedPpaPayments ?? false
  const { resolver, defaultValues } = useAddPaymentSchema(loanActivationDate, initialAmount, canBypassAcpLock)
  const { control, handleSubmit, setValue, watch } = useForm<EditPaymentSchemaType>({
    resolver,
    defaultValues,
  })
  const { dialogProps, closeDialog } = useUnmountableDialog(onClose)
  const dollarAdornment = useDollarAdornment()

  const paymentMethod = watch('paymentMethod')

  const handlePaymentMethodChange = useCallback(() => {
    setValue('sendSmsReminder', false, {
      shouldValidate: true,
    })
  }, [setValue])

  const handleConfirm = handleSubmit((data) => {
    onConfirm(data)
    closeDialog()
  })

  const minStartDate = useMemo(
    () =>
      SafePaymentActionDateService.getInstance().getMinPaymentDate(
        paymentMethod,
        loanActivationDate,
        canBypassAcpLock,
      ),
    [paymentMethod, canBypassAcpLock, loanActivationDate],
  )

  return (
    <FormDialog
      {...dialogProps}
      title={t('loan.payment.add')}
      onConfirm={handleConfirm}
      additionalActions={<EditPaymentCloseAllBtn onCloseAll={onCloseAll} />}
    >
      <Stack spacing={1}>
        <ControlledNumberField
          control={control}
          name="amount"
          slotProps={{ input: dollarAdornment }}
          label={t('loan.payment.amount')}
          autoFocus
        />
        <ControlledDateOnlyPicker
          control={control}
          name="effectiveDate"
          label={t('loan.payment.date')}
          minDate={minStartDate}
        />
        <ControlledSelect
          control={control}
          name="paymentMethod"
          options={paymentMethodOptions}
          label={t('loan.paymentMethod')}
          disableEmptyOption
          onChange={handlePaymentMethodChange}
        />
        <ControlledTextField control={control} name="note" label="Note" />
        <ControlledCheckbox
          control={control}
          name="sendSmsReminder"
          label={t('loan.payment.sendSMSReminder')}
          disabled={paymentMethod !== EPaymentMethod.PreAuthorizedPayment}
        />
      </Stack>
    </FormDialog>
  )
}

export default LoanAddPaymentDialog
